import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import {
  abandonHeldRequests,
  checkForNewToken,
  isSessionExpired,
  subscribeToSessionExpiry,
} from '../../api/sessionExpiry';

/**
 * Shown over the current page when the session expires mid-use. Signing in
 * happens in a new tab so this page, and anything typed into it, survives;
 * the request that failed is replayed once the new token lands.
 */
const SessionExpiredDialog = () => {
  const { isAuthenticated, logout } = useAuth();
  const [expired, setExpired] = useState(isSessionExpired);

  useEffect(() => subscribeToSessionExpiry(setExpired), []);

  // Nobody signed in (a sign-out, or a stale token found on page load) means
  // there is no page state worth holding: release the requests as failures.
  useEffect(() => {
    if (!isAuthenticated && expired) {
      abandonHeldRequests();
    }
  }, [isAuthenticated, expired]);

  // The storage event covers the usual case; this catches browsers that keep
  // the token in per-tab storage or miss the event while the tab is hidden.
  useEffect(() => {
    if (!expired) {
      return undefined;
    }
    const recheck = () => {
      checkForNewToken();
    };
    window.addEventListener('focus', recheck);
    document.addEventListener('visibilitychange', recheck);
    return () => {
      window.removeEventListener('focus', recheck);
      document.removeEventListener('visibilitychange', recheck);
    };
  }, [expired]);

  const openSignIn = () => {
    window.open('/login?reauth=1', '_blank', 'noopener');
  };

  const signOut = () => {
    abandonHeldRequests();
    logout();
  };

  return (
    <Dialog open={expired && isAuthenticated} maxWidth="xs" fullWidth>
      <DialogTitle>Your session expired</DialogTitle>
      <DialogContent>
        <Typography>
          Sign in again in a new tab, then come back here. What you were working
          on is still on this page and will save once you&apos;re signed in.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button color="secondary" onClick={signOut}>
          Sign out
        </Button>
        <Button variant="contained" onClick={openSignIn}>
          Sign in again
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SessionExpiredDialog;
